import cx from "classnames";
import {
  faArrowRight,
  faChevronRight,
  faRepeatAlt,
  faTag,
} from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { PromotionKind } from "common/types/promotions";
import { toLocaleDate } from "common/utils/dates";

import { ProductListSource } from "client/analytics/types";
import { useSendPostHogPromotionInfoClick } from "client/analytics/utils";
import { authoredLinkProps } from "client/routes/links";
import { ProductCardBadgePrice } from "client/products/types";
import { usePromotionDrawer } from "client/products/stackables";

import { StorefrontCollectionRebate } from "../types";

import styles from "../styles/CollectionRebateCardList.module.less";

type CollectionRebateCardListProps = {
  rebates: StorefrontCollectionRebate[];
  collectionSlug: string;
  listSource: ProductListSource;
};

type CollectionRebateCardProps = Omit<CollectionRebateCardListProps, "rebates"> & {
  rebate: StorefrontCollectionRebate;
  position: number;
};

const CollectionRebateCard = ({
  rebate,
  position,
  collectionSlug,
  listSource,
}: CollectionRebateCardProps) => {
  const { open: openPromotionDrawer } = usePromotionDrawer();
  const sendPromotionInfoClick = useSendPostHogPromotionInfoClick();
  const linkProps = rebate.url ? authoredLinkProps(rebate.url) : null;
  const isAutoship = rebate.applies_to === ProductCardBadgePrice.Subscription;
  const brand = rebate.brand.trim();
  const validThrough =
    rebate.end_date && toLocaleDate(rebate.end_date, { month: "short", day: "numeric" });
  const className = cx(styles.card, isAutoship && styles.autoship);

  const openTerms = () => {
    sendPromotionInfoClick({
      source: listSource,
      kind: PromotionKind.Rebate,
      promotion_id: rebate.rebate_id,
      collection_slug: collectionSlug,
      position,
    });
    openPromotionDrawer({
      kind: PromotionKind.Rebate,
      id: rebate.rebate_id,
      shouldDisplayItems: false,
    });
  };

  const card = (
    <>
      <span className={styles.iconBox}>
        <Icon icon={isAutoship ? faRepeatAlt : faTag} title={isAutoship ? "Autoship" : undefined} />
      </span>
      <span className={styles.body}>
        {!!brand && <span className={styles.brand}>{brand}</span>}
        <span className={styles.offer}>{rebate.chip || rebate.title}</span>
        {!!rebate.chip && <span className={styles.title}>{rebate.title}</span>}
        <span className={styles.details}>
          {rebate.promo_code ? (
            <span>
              Code <span className={styles.codeValue}>{rebate.promo_code}</span>
            </span>
          ) : (
            <span>Applied at checkout</span>
          )}
          {!!validThrough && (
            <>
              <span aria-hidden="true">·</span>
              <span>Valid through {validThrough}</span>
            </>
          )}
        </span>
      </span>
      <Icon className={styles.arrow} icon={linkProps ? faArrowRight : faChevronRight} />
    </>
  );

  if (linkProps) {
    return (
      <LinkButton {...linkProps} className={className}>
        {card}
      </LinkButton>
    );
  }
  return (
    <LinkButton type="button" className={className} onClick={openTerms}>
      {card}
    </LinkButton>
  );
};

/** The page's highlighted rebates as a row of cards under the hero; nothing when there are none. */
const CollectionRebateCardList = ({
  rebates,
  collectionSlug,
  listSource,
}: CollectionRebateCardListProps) =>
  rebates.length ? (
    <div className={styles.list}>
      {rebates.map((rebate, position) => (
        <CollectionRebateCard
          key={rebate.id}
          rebate={rebate}
          position={position}
          collectionSlug={collectionSlug}
          listSource={listSource}
        />
      ))}
    </div>
  ) : null;

export default CollectionRebateCardList;
